'use client';

import { useEffect } from 'react';

export type FiltersOverlayProps = Readonly<{
  isVisible: boolean;
  onClose: () => void;
}>;

/**
 * Overlay component that provides a blurred background and handles click-outside-to-close functionality.
 *
 * @param {FiltersOverlayProps} props - The component props.
 *
 * @returns {JSX.Element | null} The rendered component or null if not visible.
 */
export function FiltersOverlay({ isVisible, onClose }: FiltersOverlayProps) {
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isVisible]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVisible) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isVisible, onClose]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 999
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}></div>
  );
}
